import {css} from 'aphrodite';
import classNames from 'classnames';
import React, {Dispatch, SetStateAction, useState} from 'react';

import {
  GeneralParametersBLASTParamsSection,
  ProgramTypeBLASTParamsSection,
  ScoringBLASTParamsSection,
} from 'search/blast/BLASTParamsSection';
import BLASTQueryChips from 'search/blast/BLASTQueryChips';
import {BLASTInputSource} from 'search/blast/BLASTTracking';
import {styles} from 'search/blast/constants';
import {
  getCandidateBLASTUiParamsFromFilter,
  getDefaultBLASTParamsForTask,
} from 'search/blast/parameters/uiParamHelpers';
import {BLASTFilter, hasTaskParam, Operator} from 'search/SearchConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';

export interface SetParametersPaneProps {
  filter: BLASTFilter;
  setFilter: Dispatch<SetStateAction<BLASTFilter>>;
  setInputSources?: Dispatch<SetStateAction<Record<string, BLASTInputSource>>>;
  shortProgram?: boolean;
}

export default function SetParametersPane({
  filter,
  setFilter,
  setInputSources,
}: SetParametersPaneProps): JSX.Element {
  const [candidateParams, setCandidateParams] = useState(getCandidateBLASTUiParamsFromFilter(filter));

  return (
    <div>
      <BLASTQueryChips
        filter={filter}
        onRemoveSequence={(sequenceToRemove: string, type: 'sequence' | 'entity') => {
          if (type === 'sequence') {
            setFilter((prevFilter) => ({
              ...prevFilter,
              value: {
                ...prevFilter.value,
                textSequences: prevFilter.value.textSequences.filter((ts) => ts.name !== sequenceToRemove),
              },
            }));
          } else {
            setFilter((prevFilter) => ({
              ...prevFilter,
              value: {
                ...prevFilter.value,
                apiIdentifiers: prevFilter.value.apiIdentifiers.filter((id) => id !== sequenceToRemove),
              },
            }));
          }
          if (setInputSources) {
            setInputSources((prevSources) => {
              const newSources = {...prevSources};
              delete newSources[sequenceToRemove];
              return newSources;
            });
          }
        }}
        title="Review sequences"
        canRemoveLastSequence={false}
      />
      {filter.operator !== Operator.TBLASTX && (
        <ProgramTypeBLASTParamsSection
          candidateParams={candidateParams}
          setCandidateParams={setCandidateParams}
          filter={filter}
          setFilter={setFilter}
        />
      )}
      <GeneralParametersBLASTParamsSection
        candidateParams={candidateParams}
        filter={filter}
        setFilter={setFilter}
      />
      <ScoringBLASTParamsSection
        candidateParams={candidateParams}
        setCandidateParams={setCandidateParams}
        filter={filter}
        setFilter={setFilter}
      />
      <Button
        buttonStyle={ButtonStyle.LINK}
        text="Reset to algorithm defaults"
        className={classNames('u-moderate-spacing-bottom', css(styles.withoutButtonFocusStyling))}
        onClick={(): void => {
          const newFilter: BLASTFilter = {
            ...filter,
            params: getDefaultBLASTParamsForTask(
              filter,
              filter.params && hasTaskParam(filter.params) ? filter.params.task : undefined
            ),
          };
          setCandidateParams(getCandidateBLASTUiParamsFromFilter(newFilter));
          setFilter(newFilter);
        }}
      />
    </div>
  );
}
